// signals-web.ts — the WEB runtime's journal signals.
//
// 🔴 WHY THIS EXISTS: `week_meta` returned [] and `week_signals` was a zero stub in
// web-commands.ts. On web the reflection UI showed nothing and the BILLED reflection was handed a
// journal with no entries in it. Native is unaffected (Rust computes it in db.rs).
//
// Every rule below mirrors `src-tauri/src/db.rs::week_signals` and its helpers. Where the two could
// drift, the Rust wins — it is the shipping surface.
//
// ⚠️ METADATA ONLY, AND THAT IS THE POINT. The Rust query selects title, mood, timestamp and
// `length(body)` — a NUMBER. The body text is never selected and never leaves the device. This port
// takes the same care: it reads `body.length` and never the body itself. Keywords come from TITLES
// the user chose, not from what they wrote.
//
// ⚠️ OBSERVATIONAL. A journal is mental-health-adjacent: these facts describe counts, means and
// deltas about someone's own entries. Never advice, never a diagnosis, never "keep it up".

import type { JournalEntry as Entry } from "./store";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Mirror of db.rs::STOPWORDS — words that carry no signal in a title. */
const STOPWORDS = new Set([
  "the", "a", "an", "and", "or", "but", "of", "to", "in", "on", "at", "for", "with", "my", "me",
  "i", "is", "was", "it", "this", "that", "today", "day", "untitled", "about", "as", "be", "so",
  "we", "you", "your", "im", "ive", "from", "by", "are", "out", "up", "no", "not", "do", "did",
]);

const round2 = (x: number) => Math.round(x * 100) / 100;
const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);
function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const n = s.length;
  return n % 2 ? s[n / 2 | 0] : (s[n / 2 - 1] + s[n / 2]) / 2;
}
/** Mirror of db.rs::join_and. */
function joinAnd(items: string[]): string {
  if (!items.length) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}
/** Monday-indexed weekday, matching chrono's num_days_from_monday. */
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;
/** Whole-day key, matching chrono's num_days_from_ce for grouping/streak purposes. */
const dayKey = (d: Date) => Math.floor(d.getTime() / 86400000);

interface Row { title: string; mood: number; ts: Date; chars: number }

const toRows = (entries: Entry[], cutoff: number): Row[] =>
  entries
    .map((e) => ({
      title: e.title ?? "",
      mood: Number(e.mood ?? 0),
      ts: new Date(Date.parse(e.created_at)),
      // The body's LENGTH only — never the text. Same discipline as the Rust query.
      chars: typeof e.body === "string" ? e.body.length : 0,
    }))
    .filter((r) => !Number.isNaN(r.ts.getTime()) && r.ts.getTime() >= cutoff)
    .sort((a, b) => a.ts.getTime() - b.ts.getTime());

/** Mirror of db.rs::week_meta — the past 7 days' title + mood + timestamp, oldest first. */
export function weekMetaWeb(entries: Entry[]) {
  const cutoff = Date.now() - 7 * 86400000;
  return toRows(entries, cutoff).map((r) => ({
    title: r.title, mood: r.mood, timestamp: r.ts.toISOString(),
  }));
}

/** Mirror of db.rs::week_signals. */
export function weekSignalsWeb(entries: Entry[], windowDays: number) {
  const window = Math.max(1, windowDays);
  const rows = toRows(entries, Date.now() - window * 86400000);

  const empty = {
    schema:
      "solo_journal.week_signals.v2: derived aggregates computed ON-DEVICE over the user's OWN journal METADATA only (mood 1-5, entry title, timestamp, and the body's CHAR-length — the body text NEVER leaves the device; title_keywords come from TITLES the user chose). Covers `window_days`; `recent` compares the last 7 days to the rest of the window (the baseline). headline_facts = pre-ranked, ready-to-say OBSERVATIONAL sentences already carrying the figure + unit; ground the reflection in THESE and never invent a number. Describe these facts back; NEVER advise, diagnose, instruct, or judge. Mood is 1 (low) to 5 (high).",
    window_days: window,
    entry_count: rows.length,
    active_days: 0,
    mood: { mean: 0, median: 0, variance: 0, min: 0, max: 0, distribution: {} as Record<number, number> },
    by_weekday: [] as { weekday: string; entries: number; mean_mood: number }[],
    title_keywords: [] as { word: string; count: number }[],
    cadence: { current_streak: 0, longest_run: 0, gap_days: 0, entries_per_active_day: 0 },
    length: { mean_chars: 0, median_chars: 0, min_chars: 0, max_chars: 0 },
    recent: {
      recent_entries: 0, baseline_entries: 0, recent_mean_mood: 0, baseline_mean_mood: 0,
      mood_delta: 0, mood_direction: "flat", recent_per_week: 0, baseline_per_week: 0,
      cadence_direction: "same",
    },
    extremes: { highest_mood: 0, highest_weekday: "", lowest_mood: 0, lowest_weekday: "", mood_range: 0 },
    headline_facts: [] as { kind: string; text: string; tone: string }[],
  };
  if (!rows.length) return empty;

  // ── Mood ───────────────────────────────────────────────────────────────────
  const moods = rows.map((r) => r.mood);
  const m = mean(moods);
  const distribution: Record<number, number> = {};
  for (const r of rows) distribution[r.mood] = (distribution[r.mood] ?? 0) + 1;
  const mood = {
    mean: round2(m),
    median: round2(median(moods)),
    variance: round2(moods.reduce((a, x) => a + (x - m) ** 2, 0) / moods.length),
    min: Math.min(...moods),
    max: Math.max(...moods),
    distribution,
  };

  // ── Mood by weekday (Mon..Sun, only days that have entries) ────────────────
  const wk = new Map<number, number[]>();
  for (const r of rows) {
    const i = mondayIndex(r.ts);
    wk.set(i, [...(wk.get(i) ?? []), r.mood]);
  }
  const by_weekday = WD.map((name, i) => ({ name, i, v: wk.get(i) }))
    .filter((x) => x.v)
    .map((x) => ({ weekday: x.name, entries: (x.v as number[]).length, mean_mood: round2(mean(x.v as number[])) }));

  // ── Title keywords — TITLES ONLY, each word counted once per title ─────────
  const counts = new Map<string, number>();
  for (const r of rows) {
    const seen = new Set<string>();
    for (const tok of r.title.split(/[^a-zA-Z0-9]+/)) {
      const w = tok.toLowerCase();
      if (w.length < 3 || STOPWORDS.has(w) || (w !== "" && !Number.isNaN(Number(w)))) continue;
      if (!seen.has(w)) { seen.add(w); counts.set(w, (counts.get(w) ?? 0) + 1); }
    }
  }
  const title_keywords = [...counts.entries()]
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count || a.word.localeCompare(b.word))
    .slice(0, 3);

  // ── Cadence: active days, longest run, gaps, current streak ────────────────
  const days = [...new Set(rows.map((r) => dayKey(r.ts)))].sort((a, b) => a - b);
  const active_days = days.length;
  let longest = 1, run = 1;
  for (let i = 1; i < days.length; i++) {
    if (days[i] - days[i - 1] === 1) { run += 1; longest = Math.max(longest, run); }
    else run = 1;
  }
  const span = days[days.length - 1] - days[0] + 1;
  const gap_days = Math.max(0, span - active_days);
  const activeSet = new Set(days);
  let cursor = dayKey(new Date());
  if (!activeSet.has(cursor)) cursor -= 1;   // a streak may end yesterday
  let current = 0;
  while (activeSet.has(cursor)) { current += 1; cursor -= 1; }
  const cadence = {
    current_streak: current,
    longest_run: longest,
    gap_days,
    entries_per_active_day: round2(rows.length / active_days),
  };

  // ── Length — CHAR COUNTS only ──────────────────────────────────────────────
  const chars = rows.map((r) => r.chars);
  const length = {
    mean_chars: round2(mean(chars)),
    median_chars: round2(median(chars)),
    min_chars: Math.min(...chars),
    max_chars: Math.max(...chars),
  };

  // ── Recent (last 7 days) vs the baseline (the rest of the window) ──────────
  const recentCutoff = Date.now() - 7 * 86400000;
  const recentMoods = rows.filter((r) => r.ts.getTime() >= recentCutoff).map((r) => r.mood);
  const baseMoods = rows.filter((r) => r.ts.getTime() < recentCutoff).map((r) => r.mood);
  const recentN = recentMoods.length, baseN = baseMoods.length;
  const recentMean = mean(recentMoods), baseMean = mean(baseMoods);
  // A direction is only meaningful when BOTH sides have entries — otherwise "flat".
  const moodDelta = recentN > 0 && baseN > 0 ? round2(recentMean - baseMean) : 0;
  const moodDirection = recentN === 0 || baseN === 0 ? "flat"
    : moodDelta >= 0.3 ? "up" : moodDelta <= -0.3 ? "down" : "flat";
  const baseSpanDays = Math.max(1, window - 7);
  const recentPerWeek = round2(recentN);              // the last 7 days IS one week
  const baselinePerWeek = round2((baseN * 7) / baseSpanDays);
  const cadenceDirection = baseN === 0 ? "same"
    : recentPerWeek >= baselinePerWeek + 1 ? "more"
    : recentPerWeek <= baselinePerWeek - 1 ? "less" : "same";
  const recent = {
    recent_entries: recentN, baseline_entries: baseN,
    recent_mean_mood: round2(recentMean), baseline_mean_mood: round2(baseMean),
    mood_delta: moodDelta, mood_direction: moodDirection,
    recent_per_week: recentPerWeek, baseline_per_week: baselinePerWeek,
    cadence_direction: cadenceDirection,
  };

  // ── Mood extremes ──────────────────────────────────────────────────────────
  const hi = rows.reduce((a, b) => (b.mood > a.mood ? b : a));
  const lo = rows.reduce((a, b) => (b.mood < a.mood ? b : a));
  const extremes = {
    highest_mood: hi.mood, highest_weekday: WD[mondayIndex(hi.ts)],
    lowest_mood: lo.mood, lowest_weekday: WD[mondayIndex(lo.ts)],
    mood_range: hi.mood - lo.mood,
  };

  const sig = { ...empty, active_days, mood, by_weekday, title_keywords, cadence, length, recent, extremes };
  return { ...sig, headline_facts: buildHeadlineFacts(sig) };
}

/** Mirror of db.rs::build_headline_facts. Capped at 7, most informative first. */
function buildHeadlineFacts(s: {
  entry_count: number; active_days: number; window_days: number;
  mood: { mean: number; variance: number };
  by_weekday: { weekday: string; mean_mood: number }[];
  title_keywords: { word: string; count: number }[];
  cadence: { current_streak: number };
  length: { mean_chars: number; min_chars: number; max_chars: number };
  recent: {
    recent_entries: number; baseline_entries: number; recent_mean_mood: number;
    baseline_mean_mood: number; mood_delta: number; mood_direction: string;
    baseline_per_week: number; cadence_direction: string;
  };
  extremes: { highest_mood: number; highest_weekday: string; lowest_mood: number; lowest_weekday: string; mood_range: number };
}) {
  const facts: { kind: string; text: string; tone: string }[] = [];
  if (s.entry_count === 0) return facts;
  const entriesWord = (n: number) => (n === 1 ? "entry" : "entries");
  const daysWord = (n: number) => (n === 1 ? "day" : "days");

  // 1) Scale + cadence.
  facts.push({
    kind: "cadence",
    text: `You wrote ${s.entry_count} ${entriesWord(s.entry_count)} on ${s.active_days} ${daysWord(s.active_days)} over the last ${s.window_days} days.`,
    tone: "neutral",
  });

  // 2) Recent mood against the user's own baseline.
  if (s.recent.recent_entries > 0 && s.recent.baseline_entries > 0 && s.recent.mood_direction !== "flat") {
    facts.push({
      kind: "trend",
      text: `Your last 7 days averaged ${s.recent.recent_mean_mood.toFixed(1)}/5 — ${Math.abs(s.recent.mood_delta).toFixed(1)} ${s.recent.mood_direction === "up" ? "above" : "below"} your ${s.recent.baseline_mean_mood.toFixed(1)}/5 across the weeks before.`,
      tone: s.recent.mood_direction,
    });
  } else if (s.recent.recent_entries > 0 && s.recent.baseline_entries > 0) {
    facts.push({
      kind: "trend",
      text: `Your mood held steady — ${s.recent.recent_mean_mood.toFixed(1)}/5 this week against ${s.recent.baseline_mean_mood.toFixed(1)}/5 before.`,
      tone: "neutral",
    });
  }

  // 3) Overall level + spread.
  const swing = s.mood.variance < 0.5 ? "and it stayed steady"
    : s.mood.variance < 1.5 ? "with some day-to-day swing"
    : "with a wide day-to-day swing";
  facts.push({
    kind: "mood",
    text: `Your mood averaged ${s.mood.mean.toFixed(1)}/5 across these ${s.entry_count} ${entriesWord(s.entry_count)} ${swing}.`,
    tone: "neutral",
  });

  // 4) Writing-frequency shift.
  if (s.recent.baseline_entries > 0 && s.recent.cadence_direction !== "same") {
    const more = s.recent.cadence_direction === "more";
    facts.push({
      kind: "rhythm",
      text: `You wrote ${s.recent.recent_entries} ${s.recent.recent_entries === 1 ? "entry" : "entries"} this week — ${more ? "more often" : "less often"} than about ${s.recent.baseline_per_week.toFixed(0)} a week before.`,
      tone: more ? "up" : "down",
    });
  }

  // 5) Weekday rhythm — only when the gap is real.
  if (s.by_weekday.length >= 3) {
    const p = s.by_weekday.reduce((a, b) => (b.mean_mood > a.mean_mood ? b : a));
    const t = s.by_weekday.reduce((a, b) => (b.mean_mood < a.mean_mood ? b : a));
    if (p.mean_mood - t.mean_mood >= 0.5) {
      facts.push({
        kind: "rhythm",
        text: `${p.weekday} read highest at ${p.mean_mood.toFixed(1)}/5, ${t.weekday} lowest at ${t.mean_mood.toFixed(1)}/5.`,
        tone: "neutral",
      });
    }
  }

  // 6) The notable single high/low.
  if (s.extremes.mood_range >= 2) {
    facts.push({
      kind: "extreme",
      text: `Your high was a ${s.extremes.highest_mood} on ${s.extremes.highest_weekday} and your low a ${s.extremes.lowest_mood} on ${s.extremes.lowest_weekday} — a ${s.extremes.mood_range}-point spread.`,
      tone: "neutral",
    });
  }

  // 7) Current streak — stated plainly, never "keep it up".
  if (s.cadence.current_streak >= 2) {
    facts.push({ kind: "cadence", text: `You're on a ${s.cadence.current_streak}-day writing streak.`, tone: "up" });
  }

  // 8) Recurring title threads — from TITLES the user chose.
  if (s.title_keywords.length) {
    const top = s.title_keywords[0];
    if (top.count >= 2) {
      const extras = s.title_keywords.slice(1).filter((k) => k.count >= 2).slice(0, 2).map((k) => `"${k.word}"`);
      let t = `"${top.word}" showed up in ${top.count} of your titles`;
      if (extras.length) t += `, alongside ${joinAnd(extras)}`;
      facts.push({ kind: "keyword", text: `${t}.`, tone: "neutral" });
    }
  }

  // 9) Entry length — CHAR counts only.
  if (s.length.mean_chars >= 1) {
    facts.push({
      kind: "length",
      text: `Your entries ran about ${Math.round(s.length.mean_chars)} characters on average, from ${s.length.min_chars} to ${s.length.max_chars}.`,
      tone: "neutral",
    });
  }

  return facts.slice(0, 7);
}
